/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Accordion, AccordionGroup, AccordionHeader, AccordionPanel, Typography2 } from '@coursera/cds-core';

import type {
  GetCoursesWithSessionsQuery_CoursesV1Resource_multiGet_elements as Course,
  GetCoursesWithSessionsQuery_CoursesV1Resource_multiGet_elements_activeAndUpcomingSessions_elements as Session,
} from 'bundles/degree-home/components/SessionJoinDialog/utils/__generated__/GetCoursesWithSessionsQuery';

import _t from 'i18n!nls/course-sessions';

import { SessionRadioGroup } from './SingleCourseSelectView';

type Props = {
  courses: Course[];
  enrollingCourseId?: string;
  onSelectChange?: (courseId: string, selectedSession: Session) => void;
};

const styles = {
  accordionGroup: () => css`
    > *:not(:first-of-type) {
      margin: 0;
      border-top: 1px solid var(--cds-color-grey-50);
    }
  `,
  accordionPanel: css`
    padding-left: 2rem;
  `,
};

const MultipleCoursesSelectView = ({ courses, enrollingCourseId, onSelectChange }: Props) => {
  const [selectedSessions, setSelectedSessions] = useState<Session[]>([]);
  const enrollingCourseIndex = courses.findIndex((course: Course) => {
    return course.id === enrollingCourseId;
  });
  const defaultExpandedAccordions = enrollingCourseIndex === -1 ? [0] : [enrollingCourseIndex];

  const onRadioGroupSelectChange = (courseId: string, session: Session) => {
    const courseIndex = courses.findIndex((course) => course.id === courseId);
    if (courseIndex !== -1) {
      setSelectedSessions((prev) => {
        const prevState = [...prev];
        prevState[courseIndex] = session;
        return prevState;
      });
      onSelectChange?.(courseId, session);
    }
  };

  return (
    <AccordionGroup defaultExpanded={defaultExpandedAccordions} css={styles.accordionGroup}>
      {courses.map((course: Course, idx: number) => (
        <Accordion variant="silent" key={course.id}>
          <AccordionHeader label={course.name}>
            {selectedSessions[idx] && (
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {_t('#{startDate} to #{endDate}', {
                  startDate: formatDateTimeDisplay(selectedSessions[idx]?.startsAt as number, MED_DATE_ONLY_DISPLAY),
                  endDate: formatDateTimeDisplay(selectedSessions[idx]?.endsAt as number, MED_DATE_ONLY_DISPLAY),
                })}
              </Typography2>
            )}
          </AccordionHeader>
          <AccordionPanel css={styles.accordionPanel}>
            <SessionRadioGroup
              course={course}
              onSelectChange={onRadioGroupSelectChange}
              label={_t("Select when you'd like to take this course")}
            />
          </AccordionPanel>
        </Accordion>
      ))}
    </AccordionGroup>
  );
};

export default MultipleCoursesSelectView;
